import React, { useCallback } from "react"
import { useApplicationState } from "~/hooks/useApplicationState"
import { useHasMounted } from "~/hooks/useHasMounted"
import CookieConsentBanner from "./CookieConsentBanner"
import {
  CookieConsentChoice,
  CookieConsentPreferences,
  CookieConsentRecord,
  COOKIE_CONSENT_ACCEPT_ALL,
  COOKIE_CONSENT_DEFAULT_PREFERENCES,
  COOKIE_CONSENT_PRIVACY_SIGNAL_PREFERENCES,
  COOKIE_CONSENT_REJECT_ALL,
} from "./CookieConsentTypes"
import { useCookieConsentReopen } from "./hooks/useCookieConsentReopen"
import { hasGlobalPrivacyControl } from "./hooks/useCookieConsentStorage"

interface Props {
  isEnabled: boolean
  previousConsent?: CookieConsentRecord
  saveConsent: (
    choice: CookieConsentChoice,
    preferences: CookieConsentPreferences
  ) => CookieConsentRecord
}

const CookieConsent: React.FC<Props> = ({
  isEnabled,
  previousConsent,
  saveConsent,
}) => {
  const application = useApplicationState()
  const hasMounted = useHasMounted()
  const { isReopened, close } = useCookieConsentReopen()

  // CookieConsentWatcher pushes the saved choice to the dataLayer
  const updateCookieConsent = useCallback(
    (choice: CookieConsentChoice, preferences: CookieConsentPreferences) => {
      saveConsent(choice, preferences)
      close()
    },
    [saveConsent, close]
  )

  const onAcceptAll = useCallback(() => {
    updateCookieConsent("acceptAll", COOKIE_CONSENT_ACCEPT_ALL)
  }, [updateCookieConsent])

  const onRejectAll = useCallback(() => {
    updateCookieConsent("rejectAll", COOKIE_CONSENT_REJECT_ALL)
  }, [updateCookieConsent])

  const onSavePreferences = useCallback(
    (preferences: CookieConsentPreferences) => {
      updateCookieConsent("custom", preferences)
    },
    [updateCookieConsent]
  )

  if (!isEnabled) {
    return null
  }

  // the privacy signal is on and the visitor has not allowed sharing since
  const isPrivacySignalOn =
    hasMounted &&
    hasGlobalPrivacyControl() &&
    !previousConsent?.preferences.marketing

  // show when no choice is saved yet or a component asked to reopen it
  const isOpen =
    isEnabled && hasMounted && (previousConsent === undefined || isReopened)

  // nothing is shared until the visitor allows it under the privacy signal
  const unsavedPreferences = isPrivacySignalOn
    ? COOKIE_CONSENT_PRIVACY_SIGNAL_PREFERENCES
    : COOKIE_CONSENT_DEFAULT_PREFERENCES

  return (
    <CookieConsentBanner
      // remount on reopen so the banner starts from the saved choice
      key={isReopened ? "reopened" : "initial"}
      isOpen={isOpen}
      initialView={isReopened ? "customize" : "summary"}
      isPrivacySignalOn={isPrivacySignalOn}
      defaultPreferences={previousConsent?.preferences ?? unsavedPreferences}
      privacyPolicyUrl={application.privacyPolicyUrl ?? ""}
      onAcceptAll={onAcceptAll}
      onRejectAll={onRejectAll}
      onSavePreferences={onSavePreferences}
    />
  )
}

export default CookieConsent
